import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ProductVariant } from '../../utils/variantIdForSize';

export interface ComboProduct {
  id: string;
  name: string;
  price: string;
  regular_price: string;
  /** Legacy single-image field (kept for backward compatibility). */
  primary_image_url?: string;
  /** Gallery of product images; first entry is the primary image. */
  image_urls?: string[];
  product_image_link?: string;
  video_url?: string;
  color: string;
  size: string;
  variant_id: string;
  variant_ids?: Record<string, string>;
  /** Sizes with their Shopify variant IDs, e.g. [{ size: "S - 38", variant_id: "123" }] */
  variants?: ProductVariant[];
  recommended_size?: string;
  recommended_variant_id?: string;
  rating?: number;
  review_count?: number;
  product_tags?: string[];
  /** JSON-encoded map of spec title -> description, e.g. '{ "Breathable build": "..." }' */
  fabric_details?: string;
  hex_code?: string;
  url?: string;
  type: string;
}

export interface ComboShoes {
  combo_id: string; // 'brown_combo' | 'black_combo'
  name: string;
  color?: string;
  hex_code?: string;
  price: string | number;
  regular_price?: string;
  /** Legacy single-image field (kept for backward compatibility). */
  image_url?: string;
  /** Gallery of product images; first entry is the primary image. */
  image_urls?: string[];
  url?: string;
  recommended_size?: string;
  product_image_link?: string;
  rating?: number;
  review_count?: number;
  product_tags?: string[];
  /** JSON-encoded map of spec title -> description, e.g. '{ "Breathable build": "..." }' */
  fabric_details?: string;
  video_url?: string;
}

interface ComboState {
  // User profile data for avatar generation
  selfieBase64: string;
  selfieUrl: string;
  name: string;
  mobile: string;
  height: string;
  weight: string;
  bodyType: string;
  // Analysis metadata
  hairColor: string;
  skinTone: string;
  eyeColor: string;
  faceType: string;
  // Recommended combo products
  shirt: ComboProduct | null;
  pant: ComboProduct | null;
  shoes: ComboShoes | null;
  // Cached generated avatar URL
  finalLookUrl: string | null;
}

export const comboInitialState: ComboState = {
  selfieBase64: '',
  selfieUrl: '',
  name: '',
  mobile: '',
  height: '',
  weight: '',
  bodyType: '',
  hairColor: '',
  skinTone: '',
  eyeColor: '',
  faceType: '',
  shirt: null,
  pant: null,
  shoes: null,
  finalLookUrl: null,
};

const comboSlice = createSlice({
  name: 'combo',
  initialState: comboInitialState,
  reducers: {
    setComboProfile: (state, action: PayloadAction<Partial<ComboState>>) => {
      Object.assign(state, action.payload);
    },
    setComboResult: (state, action: PayloadAction<{
      shirt: ComboProduct | null;
      pant: ComboProduct | null;
      shoes: ComboShoes | null;
      hair_color?: string;
      skin_tone?: string;
      eye_color?: string;
      face_type?: string;
    }>) => {
      state.shirt = action.payload.shirt;
      state.pant = action.payload.pant;
      state.shoes = action.payload.shoes;
      if (action.payload.hair_color) state.hairColor = action.payload.hair_color;
      if (action.payload.skin_tone) state.skinTone = action.payload.skin_tone;
      if (action.payload.eye_color) state.eyeColor = action.payload.eye_color;
      if (action.payload.face_type) state.faceType = action.payload.face_type;
    },
    setComboFinalLookUrl: (state, action: PayloadAction<string>) => {
      state.finalLookUrl = action.payload;
    },
    clearCombo: () => comboInitialState,
  },
});

export const { setComboProfile, setComboResult, setComboFinalLookUrl, clearCombo } = comboSlice.actions;
export default comboSlice.reducer;
